import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { LayoutGrid, Save, Eye, RotateCcw, ArrowUp, ArrowDown, GripVertical, BarChart3 } from "lucide-react";
import { ICON_NAMES, HubIcon } from "../../../components/HubIcon";

const VIS = ["visible", "hidden", "coming_soon"];
const ROLES = ["everyone", "customers", "affiliates", "creators", "sellers", "dropshipping", "wholesale", "education", "admins"];
const ACTIONS = ["shop", "open", "explore", "learn", "track", "join", "start_selling"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminFeatureHub() {
  const [sections, setSections] = useState([]);
  const [cards, setCards] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [drag, setDrag] = useState(null);

  const load = async () => {
    const { data } = await adminClient().get("/admin/hub");
    setSections(data.sections); setCards(data.cards);
  };
  useEffect(() => {
    load().catch(() => toast.error("Could not load hub."));
    adminClient().get("/admin/hub/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, []);

  const editCard = (id, patch) => setCards((cs) => cs.map((c) => c.id === id ? { ...c, ...patch } : c));

  const saveCard = async (c) => {
    try {
      await adminClient().put(`/admin/hub/card/${c.id}`, {
        title: c.title, description: c.description, icon: c.icon, route: c.route, action: c.action,
        external: c.external, visibility: c.visibility, roles: c.roles, gated: c.gated,
        gated_reason: c.gated_reason, show_locked: c.show_locked,
      });
      toast.success(`Saved "${c.title.en}"`);
    } catch { toast.error("Could not save card."); }
  };

  const moveSection = async (idx, dir) => {
    const arr = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSections(arr);
    await adminClient().post("/admin/hub/reorder", { ordered_section_keys: arr.map((s) => s.key) }).catch(() => {});
  };

  const onDrop = async (sectionKey, targetId) => {
    if (!drag || drag.section !== sectionKey) { setDrag(null); return; }
    const inSec = cards.filter((c) => c.section === sectionKey);
    const rest = cards.filter((c) => c.section !== sectionKey);
    const fromIdx = inSec.findIndex((c) => c.id === drag.id);
    const toIdx = inSec.findIndex((c) => c.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDrag(null); return; }
    const moved = inSec.splice(fromIdx, 1)[0];
    inSec.splice(toIdx, 0, moved);
    const reordered = inSec.map((c, i) => ({ ...c, order: i }));
    setCards([...rest, ...reordered]);
    setDrag(null);
    await adminClient().post("/admin/hub/reorder", { section: sectionKey, ordered_card_ids: reordered.map((c) => c.id) }).catch(() => {});
    toast.success("Order saved");
  };

  const reseed = async () => {
    if (!window.confirm("Reset all Hub cards to defaults? Custom edits will be lost.")) return;
    await adminClient().post("/admin/hub/reseed").catch(() => {});
    load(); toast.success("Hub reset to defaults.");
  };

  return (
    <div data-testid="admin-feature-hub">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Feature Hub</h1></div>
        <div className="flex gap-2">
          <a href="/hub" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 border border-black/15 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray" data-testid="hub-preview-btn"><Eye className="w-4 h-4" /> Preview</a>
          <button onClick={reseed} className="flex items-center gap-1.5 border border-black/15 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray" data-testid="hub-reseed-btn"><RotateCcw className="w-4 h-4" /> Reset</button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Show, hide, edit, reorder and restrict Hub feature cards — no code deploy needed. Drag cards to reorder within a section.</p>

      {analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="hub-analytics">
          <Stat label="Total events" value={analytics.total_events} />
          <Stat label="Failed route taps" value={analytics.failed_route_attempts} />
          <Stat label="Top feature" value={analytics.top_features?.[0]?.card_id || "—"} small />
          <Stat label="Tracked features" value={Object.keys(analytics.by_card || {}).length} />
        </div>
      )}

      {sections.map((sec, si) => (
        <section key={sec.key} className="mb-8" data-testid={`hub-admin-section-${sec.key}`}>
          <div className="flex items-center gap-2 border-b-2 border-taven-gold/40 pb-2 mb-4">
            <h2 className="text-base font-black font-display uppercase tracking-wide">{sec.title.en}</h2>
            <div className="ml-auto flex gap-1">
              <button onClick={() => moveSection(si, -1)} className="p-1 border border-black/15 hover:bg-taven-gray" data-testid={`section-up-${sec.key}`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => moveSection(si, 1)} className="p-1 border border-black/15 hover:bg-taven-gray" data-testid={`section-down-${sec.key}`}><ArrowDown className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {cards.filter((c) => c.section === sec.key).sort((a, b) => a.order - b.order).map((c) => (
              <div key={c.id} draggable onDragStart={() => setDrag({ id: c.id, section: sec.key })}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(sec.key, c.id)}
                className="border border-black/10 bg-white p-3" data-testid={`hub-admin-card-${c.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <GripVertical className="w-4 h-4 text-black/30 cursor-grab" />
                  <span className="w-7 h-7 rounded-lg bg-taven-black text-taven-gold flex items-center justify-center"><HubIcon name={c.icon} className="w-4 h-4" /></span>
                  <input value={c.title.en} onChange={(e) => editCard(c.id, { title: { ...c.title, en: e.target.value } })} className="flex-1 border border-black/15 px-2 py-1 text-sm font-bold" />
                </div>
                <input value={c.description.en} onChange={(e) => editCard(c.id, { description: { ...c.description, en: e.target.value } })} placeholder="Description" className="w-full border border-black/15 px-2 py-1 text-xs mb-2" />
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select value={c.visibility} onChange={(e) => editCard(c.id, { visibility: e.target.value })} data-testid={`vis-${c.id}`} className="border border-black/15 px-2 py-1 text-xs">
                    {VIS.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
                  </select>
                  <select value={c.icon} onChange={(e) => editCard(c.id, { icon: e.target.value })} className="border border-black/15 px-2 py-1 text-xs">
                    {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <input value={c.route} onChange={(e) => editCard(c.id, { route: e.target.value })} placeholder="/route" className="border border-black/15 px-2 py-1 text-xs" data-testid={`route-${c.id}`} />
                  <select value={c.action} onChange={(e) => editCard(c.id, { action: e.target.value })} className="border border-black/15 px-2 py-1 text-xs">
                    {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <select value={c.roles?.[0] || "everyone"} onChange={(e) => editCard(c.id, { roles: [e.target.value] })} data-testid={`roles-${c.id}`} className="border border-black/15 px-2 py-1 text-xs">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!c.gated} onChange={(e) => editCard(c.id, { gated: e.target.checked })} /> Gated</label>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1 text-[11px]"><input type="checkbox" checked={!!c.show_locked} onChange={(e) => editCard(c.id, { show_locked: e.target.checked })} /> Show locked</label>
                  <button onClick={() => saveCard(c)} className="flex items-center gap-1 bg-taven-black text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid={`save-card-${c.id}`}><Save className="w-3 h-3" /> Save</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const Stat = ({ label, value, small }) => (
  <div className="border border-black/10 bg-white p-3">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><BarChart3 className="w-3 h-3" />{label}</p>
    <p className={`font-black font-display ${small ? "text-sm truncate" : "text-2xl"}`}>{value}</p>
  </div>
);
